import { useEffect, useRef, useState } from 'react'
import moment from 'moment'
import Fade from '@/components/Fade'
import { Button, Col, Input, Row, InputProps, Switch } from 'antd'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'

import './ContentComponent.style.less'
import {
  chunk,
  uniq,
  sortBy,
  concat,
  find,
  forEach,
  parseInt,
  indexOf,
  cloneDeep,
  isEqual,
  isNull,
} from 'lodash'
import ChartComponent from '../ChartComponent'

import {
  getProductionStatusReport,
  createUpdateProductionStatusReport,
} from '../../Production.service'
import ModalConfirm from '@/components/ModalConfirm/ModalConfirm'
import { getUrlParams } from '@/utils/common'
import { ProductionStatusDocumentField } from '../ProductionStatusDocumentField/ProductionStatusDocumentField'

interface PermissionProps {
  canRead: boolean
  canWrite: boolean
}

interface Props {
  tableChartTab: string
  dataPlannedDate: any
  permission: PermissionProps
  firstCreated?: boolean
  handleDisableTap: (value) => void
}

interface InputNumberType extends InputProps {
  isBold?: boolean
  actualHighlight?: 'red' | 'green'
  key?: any
}

const getStartEndOfWeek = (day: string) => {
  const dayObj = moment(day, 'YYYY-MM-DD')
  const mockStartOfWeek = dayObj.clone().isoWeekday(1)
  const mockEndOfWeek = dayObj.clone().isoWeekday(7)
  const dayRange = []

  for (
    let day = mockStartOfWeek;
    day.isSameOrBefore(mockEndOfWeek);
    day.add(1, 'day')
  ) {
    dayRange.push(day.format('YYYY-MM-DD'))
  }
  return dayRange
}

/**
 *
 * @param startDate '2024-03-13'
 * @param endDate '2024-04-01'
 * @returns ["2024-03-13","2024-03-12",......,"2024-04-01"]
 */
const handleDateIncrement = (startDate: string, endDate: string) => {
  const results = []
  let dateIncrement = startDate

  do {
    const momentIncrementDate = moment(dateIncrement)
    dateIncrement = momentIncrementDate.add(1, 'day').format('YYYY-MM-DD')

    const valueDate = moment(dateIncrement, 'YYYY-MM-DD').format('YYYY-MM-DD')
    results.push(valueDate)
  } while (moment(dateIncrement).isBefore(endDate))

  return results
}

const getDateRange = (productStatusData: any): any => {
  const { startDate, endDate } = productStatusData

  const startDateObj = moment(startDate, 'YYYY-MM-DD')
  const endDateObj = moment(endDate, 'YYYY-MM-DD')
  const todayObj = moment()
  const completedPlanned = moment(endDate).isBefore(todayObj)
  const futurePlanned = moment(startDate).isAfter(todayObj)

  const valueDate = startDateObj.format('YYYY-MM-DD')
  const getWeekOfStartDate = getStartEndOfWeek(startDate)
  const getWeekOfEndDate = getStartEndOfWeek(endDate)
  let getWeekOfToday = getStartEndOfWeek(todayObj.format('YYYY-MM-DD'))

  let resultDates = [valueDate]

  // Remove getWeekOfToday when it's future planned or completed planned
  if (futurePlanned || completedPlanned) {
    getWeekOfToday = []
  }

  /**
   * If startDate isSame endDate
   */
  if (startDateObj.isSame(endDateObj, 'day')) {
    const uniqSameDate = uniq(
      sortBy(concat(getWeekOfStartDate, getWeekOfToday))
    )
    const resTheSameDate = transformHeaderDate(uniqSameDate, productStatusData)
    return resTheSameDate
  }

  resultDates = handleDateIncrement(startDate, endDate)
  const concatAllDays = sortBy(
    concat(getWeekOfStartDate, getWeekOfToday, resultDates, getWeekOfEndDate)
  )

  const uniqDates = uniq(concatAllDays)

  const resHeaderData = transformHeaderDate(uniqDates, productStatusData)

  return resHeaderData
}

const transformDataSource = (dataSource: any, tableDataHeader: any) => {
  const mapDataSource = []
  forEach(tableDataHeader, (element) => {
    const testFind = find(dataSource, (obj) => element.dataIndex === obj.day)
    if (testFind) {
      mapDataSource.push(testFind)
    } else {
      mapDataSource.push({
        day: element.dataIndex,
        planned_lines: null,
        planned_pieces_per_line: null,
        planned_total: null,
        actual_lines: null,
        actual_pieces_per_line: null,
        actual_total: null,
        packed_total: null,
      })
    }
  })

  return mapDataSource
}

const transformHeaderDate = (dates: any, productStatusData: any) => {
  const { startDate, endDate } = productStatusData
  const todayDate = moment().format('YYYY-MM-DD')
  const dateChunk = chunk(dates, 7)
  const resDataHeader = []
  let todayWeekIndex = null
  let startWeekIndex = null
  let endWeekIndex = null
  for (let i = 0; i < dateChunk.length; i++) {
    if (indexOf(dateChunk[i], todayDate) !== -1) {
      todayWeekIndex = i
    }
    if (indexOf(dateChunk[i], startDate) !== -1) {
      startWeekIndex = i
    }
    if (indexOf(dateChunk[i], endDate) !== -1) {
      endWeekIndex = i
    }
    resDataHeader.push(
      dateChunk[i].map((elem) => ({
        title: moment(elem, 'YYYY-MM-DD').format('ddd DD MMM YYYY'),
        value: elem,
        dataIndex: elem,
      }))
    )
  }

  return { resDataHeader, todayWeekIndex, startWeekIndex, endWeekIndex }
}

export default function ContentComponent(props: Props) {
  const { dataPlannedDate, tableChartTab, permission } = props
  const todayDate = moment().format('YYYY-MM-DD')

  const completedPlanned = moment(dataPlannedDate.end_date).isBefore(todayDate)
  const futurePlanned = moment(dataPlannedDate.start_date).isAfter(todayDate)

  const [tableDataHeader, setTableDataHeader] = useState([])
  const [firstCreated, setFirstCreated] = useState(props.firstCreated)
  const [_, setReqTabKey] = useState(getUrlParams('activeTab'))
  const [tableEditable, setTableEditable] = useState<boolean>(firstCreated)
  const [pageInside, setPageInside] = useState<number>(0)

  const [disableBack, setDisableBack] = useState<boolean>(false)
  const [disableNext, setDisableNext] = useState<boolean>(false)
  const [disableButtonToday, setDisableButtonToday] =
    useState<boolean>(firstCreated)

  const inputRef = useRef(null)
  const [dataSource, setDataSource] = useState([])
  const [dataSourceTemp, setDataSourceTemp] = useState([])
  const [modalConfirmCancel, setModalConfirmCancel] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const [defaultLastDate, setDefaultLastDate] = useState('')
  const [defaultDayWeek, setDefaultDayWeek] = useState(null)
  const [defaultTodayWeekIndex, setDefaultTodayWeekIndex] = useState(null)

  const loadData = async () => {
    if (dataPlannedDate) {
      const { id } = dataPlannedDate
      const result = await getProductionStatusReport(id, tableDataHeader)
      const resDataSource = transformDataSource(result, tableDataHeader)
      setDataSource(resDataSource)
      setDataSourceTemp(resDataSource)
    }
  }

  // Set Default display week day
  const handleDefaultDayWeek = ({
    startWeekIndex,
    todayWeekIndex,
    endWeekIndex,
  }) => {
    if (futurePlanned) {
      setDefaultDayWeek(startWeekIndex)
    } else if (completedPlanned) {
      setDefaultDayWeek(endWeekIndex)
    } else {
      setDefaultDayWeek(todayWeekIndex)
    }
  }

  useEffect(() => {
    setReqTabKey((preState) => {
      if (preState !== getUrlParams('activeTab')) {
        if (!tableEditable) {
          setFirstCreated(false)
        }
        return getUrlParams('activeTab')
      }
      return preState
    })
  }, [getUrlParams('activeTab')])

  useEffect(() => {
    loadData()
  }, [dataPlannedDate, tableDataHeader])

  useEffect(() => {
    if (defaultDayWeek !== null) {
      setPageInside(defaultDayWeek)
    }
  }, [defaultDayWeek])

  useEffect(() => {
    const { start_date, end_date } = dataPlannedDate
    setDefaultLastDate(end_date)
    const psStartEndDate = Object.assign({
      startDate: start_date,
      endDate: end_date,
    })

    const { resDataHeader, todayWeekIndex, endWeekIndex, startWeekIndex } =
      getDateRange(psStartEndDate)
    setDefaultTodayWeekIndex(todayWeekIndex)
    handleDefaultDayWeek({ todayWeekIndex, endWeekIndex, startWeekIndex })

    const disableTodayButton = tableEditable || todayWeekIndex === null

    if (tableChartTab !== 'Table') {
      handleOnSwitchEdit(false)
    }

    props.handleDisableTap(tableEditable)
    setDisableButtonToday(disableTodayButton)

    // condition enable when today button clicked
    if (pageInside < resDataHeader.length - 1 || pageInside > 0) {
      setDisableBack(false)
      setDisableNext(false)
    }
    if (tableEditable || pageInside === 0) {
      setDisableBack(true)
    }

    if (tableEditable || pageInside === resDataHeader.length - 1) {
      setDisableNext(true)
    }

    setTableDataHeader(resDataHeader[pageInside])
  }, [tableEditable, tableChartTab, pageInside, firstCreated])

  useEffect(() => {
    if (inputRef.current) {
      const focusedElement = document.querySelector(
        `input[data-id="${inputRef.current}"]`
      ) as HTMLInputElement
      if (focusedElement) {
        focusedElement.focus()
      }
    }
  }, [dataSourceTemp])

  const calculateTotal = (perLines: number, piecesPerLine: number) => {
    if (perLines === null || piecesPerLine === null) return null

    return perLines * piecesPerLine
  }

  function InputNumberField(inputProps: InputNumberType) {
    const {
      value,
      isBold,
      disabled,
      actualHighlight,
      name,
      onChange,
      id,
      onFocus,
      placeholder = '-',
    } = inputProps
    const textStyle = isBold ? 'bold' : ''
    const elem = (
      <Input
        disabled={disabled || !tableEditable}
        size="large"
        placeholder={placeholder}
        onKeyPress={(event) => {
          if (!/[0-9]/.test(event.key)) {
            event.preventDefault()
          }
        }}
        value={value}
        name={name}
        data-id={id}
        id={id}
        key={id}
        onChange={onChange}
        onFocus={onFocus}
        className={`${textStyle} ${actualHighlight} ${id}`}
      />
    )

    return <div className={`cell-input`}>{elem}</div>
  }

  const handleOnChange = (
    event: any,
    key: number,
    prefix: string = 'planned'
  ) => {
    const { name, value } = event.target
    const result = cloneDeep(dataSourceTemp)
    if (prefix === 'packed') {
      result[key][name] = parseInt(value) || null
    } else {
      result[key][name] = parseInt(value) || null
      result[key][`${prefix}_total`] = calculateTotal(
        result[key][`${prefix}_lines`],
        result[key][`${prefix}_pieces_per_line`]
      )
    }
    setDataSourceTemp(result)
  }

  const thousandSeparator = (num = '') => {
    let result = num
    if (!isNull(num)) {
      result = num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }
    return result
  }

  function RenderPlannedRow() {
    return (
      <Row className="planned-row">
        <Col className="planned-description" span={4}>
          <div className="cell">
            <p className="bold-description">Planned Lines</p>
          </div>
          <div className="cell">
            <p className="bold-description">Planned Pieces per Line</p>
          </div>
          <div className="cell">
            <p className="bold-description">Planned Pieces per Day</p>
          </div>
        </Col>
        <Col span={20} className="body-cell-date">
          {dataSourceTemp?.map((elem, key) => {
            const { planned_lines, planned_pieces_per_line, planned_total } =
              elem
            const { start_date, end_date } = dataPlannedDate
            const dataIndex = tableDataHeader[key].dataIndex
            const isHighlight = dataIndex === todayDate ? 'cell-highlight' : ''
            const isLastDay =
              dataIndex === defaultLastDate ? 'cell-lastHighlight' : ''
            const disableDayBeforeStartDate =
              moment(start_date, 'YYYY-MM-DD') > moment(dataIndex, 'YYYY-MM-DD')

            const disableDayAfterEndDate =
              moment(end_date, 'YYYY-MM-DD') < moment(dataIndex, 'YYYY-MM-DD')

            return (
              <Col
                key={`planned_col${key}`}
                span={3}
                className={[isHighlight, isLastDay].join(' ')}
              >
                <div>
                  <InputNumberField
                    disabled={
                      disableDayBeforeStartDate || disableDayAfterEndDate
                    }
                    name={`planned_lines`}
                    id={`planned_lines${key}`}
                    value={
                      tableEditable
                        ? planned_lines
                        : thousandSeparator(planned_lines)
                    }
                    onChange={(event) => {
                      handleOnChange(event, key)
                    }}
                    onFocus={() => {
                      inputRef.current = `planned_lines${key}`
                    }}
                  />
                  <InputNumberField
                    disabled={
                      disableDayBeforeStartDate || disableDayAfterEndDate
                    }
                    name={`planned_pieces_per_line`}
                    id={`planned_pieces_per_line${key}`}
                    value={
                      tableEditable
                        ? planned_pieces_per_line
                        : thousandSeparator(planned_pieces_per_line)
                    }
                    onChange={(event) => {
                      handleOnChange(event, key)
                    }}
                    onFocus={() => {
                      inputRef.current = `planned_pieces_per_line${key}`
                    }}
                  />
                  <InputNumberField
                    isBold
                    disabled
                    name={`planned_total`}
                    id={`planned_total${key}`}
                    value={thousandSeparator(planned_total)}
                    onChange={(event) => handleOnChange(event, key)}
                  />
                </div>
              </Col>
            )
          })}
        </Col>
      </Row>
    )
  }
  function RenderActualRow() {
    return (
      <Row className="actual-row">
        <Col className="actual-description" span={4}>
          <div className="cell">
            <p className="bold-description">Actual Lines</p>
          </div>
          <div className="cell">
            <p className="bold-description">Actual Pieces per Lines</p>
          </div>
          <div className="cell">
            <p className="bold-description">Actual Pieces per Day</p>
          </div>
        </Col>
        <Col span={20} className="body-cell-date">
          {dataSourceTemp?.map((elem, key) => {
            const {
              actual_total,
              actual_pieces_per_line,
              actual_lines,
              planned_total,
            } = elem
            const { start_date, end_date } = dataPlannedDate

            const dataIndex = tableDataHeader[key].dataIndex
            const isHighlight = dataIndex === todayDate ? 'cell-highlight' : ''
            const isLastDay =
              dataIndex === defaultLastDate ? 'cell-lastHighlight' : ''
            const disableDayBeforeStartDate =
              moment(start_date, 'YYYY-MM-DD') > moment(dataIndex, 'YYYY-MM-DD')
            const disableDayAfterEndDate =
              moment(end_date, 'YYYY-MM-DD') < moment(dataIndex, 'YYYY-MM-DD')
            const actualHighlight =
              parseInt(actual_total) >= parseInt(planned_total)
                ? 'green'
                : 'red'

            return (
              <Col
                key={`actual_col${key}`}
                span={3}
                className={[isHighlight, isLastDay].join(' ')}
              >
                <div>
                  <InputNumberField
                    disabled={
                      disableDayBeforeStartDate || disableDayAfterEndDate
                    }
                    name={`actual_lines`}
                    id={`actual_lines${key}`}
                    value={
                      tableEditable
                        ? actual_lines
                        : thousandSeparator(actual_lines)
                    }
                    onChange={(event) => handleOnChange(event, key, 'actual')}
                    onFocus={() => {
                      inputRef.current = `actual_lines${key}`
                    }}
                  />
                  <InputNumberField
                    disabled={
                      disableDayBeforeStartDate || disableDayAfterEndDate
                    }
                    name={`actual_pieces_per_line`}
                    id={`actual_pieces_per_line${key}`}
                    value={
                      tableEditable
                        ? actual_pieces_per_line
                        : thousandSeparator(actual_pieces_per_line)
                    }
                    onChange={(event) => handleOnChange(event, key, 'actual')}
                    onFocus={() => {
                      inputRef.current = `actual_pieces_per_line${key}`
                    }}
                  />
                  <InputNumberField
                    isBold
                    actualHighlight={actualHighlight}
                    disabled
                    name={`actual_total`}
                    id={`actual_total${key}`}
                    onChange={(event) => handleOnChange(event, key)}
                    value={thousandSeparator(elem.actual_total)}
                  />
                </div>
              </Col>
            )
          })}
        </Col>
      </Row>
    )
  }

  function RenderPackedRow() {
    return (
      <Row className="packed-row">
        <Col className="packed-description" span={4}>
          <div className="cell">
            <p className="bold-description">Packed Pieces per Day</p>
          </div>
        </Col>
        <Col span={20} className="body-cell-date">
          {dataSourceTemp?.map((elem, key) => {
            const { packed_total } = elem
            const { start_date, end_date } = dataPlannedDate

            const dataIndex = tableDataHeader[key].dataIndex
            const isHighlight = dataIndex === todayDate ? 'cell-highlight' : ''
            const isLastDay =
              dataIndex === defaultLastDate ? 'cell-lastHighlight' : ''
            const disableDayBeforeStartDate =
              moment(start_date, 'YYYY-MM-DD') > moment(dataIndex, 'YYYY-MM-DD')
            const disableDayAfterEndDate =
              moment(end_date, 'YYYY-MM-DD') < moment(dataIndex, 'YYYY-MM-DD')
            return (
              <Col
                key={`packed_col${key}`}
                span={3}
                className={[isHighlight, isLastDay].join(' ')}
              >
                <div>
                  <InputNumberField
                    disabled={
                      disableDayBeforeStartDate || disableDayAfterEndDate
                    }
                    isBold
                    name={`packed_total`}
                    id={`packed_total${key}`}
                    value={
                      tableEditable
                        ? packed_total
                        : thousandSeparator(packed_total)
                    }
                    onChange={(event) => handleOnChange(event, key, 'packed')}
                    onFocus={() => {
                      inputRef.current = `packed_total${key}`
                    }}
                  />
                </div>
              </Col>
            )
          })}
        </Col>
      </Row>
    )
  }

  const handleButtonToday = () => {
    setPageInside(defaultTodayWeekIndex)
  }
  const handleOnRightPage = () => {
    setPageInside(pageInside + 1)
    setDisableBack(false)
  }
  const handleOnLeftPage = () => {
    setPageInside(pageInside - 1)
    setDisableNext(false)
  }

  const handleOnSwitchEdit = (value) => {
    const compareSource = isEqual(dataSource, dataSourceTemp)

    if (compareSource) {
      setTableEditable(value)
    } else {
      setModalConfirmCancel(true)
    }
  }

  const handleSaveRecords = async () => {
    const { id } = dataPlannedDate
    setIsLoading(true)
    const res = await createUpdateProductionStatusReport(id, dataSourceTemp)
    if (res) {
      setTableEditable(false)
      setModalConfirmCancel(false)
      setIsLoading(false)
    }
    // Todo - Alert message when request error, requesting UI
  }

  /**
   * handleOnSwitchEdit(true) When the user cancel the warning while they updating
   */
  const handleCloseModal = () => {
    handleOnSwitchEdit(true)
    setModalConfirmCancel(false)
  }

  const handleOnDiscard = () => {
    setTableEditable(false)
    setModalConfirmCancel(false)
  }

  const handleModalSave = () => {
    handleSaveRecords()
  }

  return (
    <Fade>
      <div className="table-component-container">
        <Row>
          {modalConfirmCancel && (
            <ModalConfirm
              open={modalConfirmCancel}
              loading={isLoading}
              onCancel={() => {
                handleOnDiscard()
              }}
              cancelButtonText="Discard changes"
              confirmButtonText="Save changes"
              title="You have unsaved changes"
              content={
                <div>
                  If you leave the page without saving, the changes you have
                  made will be lost.
                </div>
              }
              onClose={() => {
                handleCloseModal()
              }}
              maskClosable={false}
              onOK={() => handleModalSave()}
            />
          )}

          <Col span={8} className="left-navigation">
            <ProductionStatusDocumentField
              label="Documents"
              productionStatusId={dataPlannedDate?.id}
            />
          </Col>
          <Col span={16} className="right-navigation">
            {permission.canWrite && props.tableChartTab == 'Table' && (
              <div className="ant-form-item-label edit-switch">
                <label>Edit</label>
                <Switch checked={tableEditable} onChange={handleOnSwitchEdit} />
              </div>
            )}

            <div className="wrap-date">
              <Button
                icon={<LeftOutlined />}
                size={'large'}
                disabled={disableBack}
                onClick={() => {
                  handleOnLeftPage()
                }}
              />
              <Button
                disabled={disableButtonToday}
                className="button-today"
                size="large"
                onClick={() => handleButtonToday()}
              >
                Today
              </Button>
              <Button
                icon={<RightOutlined />}
                disabled={disableNext}
                size={'large'}
                onClick={() => {
                  handleOnRightPage()
                }}
              />
            </div>
          </Col>
        </Row>

        {props.tableChartTab == 'Table' ? (
          <div className="table-content">
            <Row className="table-header-row">
              <Col className="cell" span={4}>
                <p className="bold">Product</p>
              </Col>
              <Col span={20} className="header-date">
                {tableDataHeader?.map((elem, key) => (
                  <div key={key} className="cell">
                    <p className="bold">{elem.title}</p>
                  </div>
                ))}
              </Col>
            </Row>
            <Row className="table-body-row">
              <RenderPlannedRow />
              <RenderActualRow />
              <RenderPackedRow />
            </Row>
          </div>
        ) : (
          <ChartComponent
            datesHeader={tableDataHeader}
            planEndDate={defaultLastDate}
            data={dataSource}
          />
        )}

        {tableEditable && (
          <Row justify="end">
            <Col className="footer-table-container">
              <Button
                className="ghost-button"
                size="large"
                onClick={() => handleOnSwitchEdit(false)}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                size="large"
                loading={isLoading}
                onClick={() => handleSaveRecords()}
              >
                Save
              </Button>
            </Col>
          </Row>
        )}
      </div>
    </Fade>
  )
}
